import React, { useEffect, useState } from 'react';
import {
  ApiButton,
  Box,
  PAYLOAD_DATE_FORMAT,
  stringToPayloadFormat,
  stringToShortTime,
  TextFieldForm,
  Typography,
} from 'web-library';
import dayjs from 'dayjs';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { candidateConfigQuery } from '../../../api/candidate/config';
import { OpenPeriodConfigRequest } from '../../../api/candidate/config/model';
import { useFeatureConfiguration } from '../logic/useFeatureConfiguration';
import { usePrevious } from '../../../hooks/usePrevious';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

const useStyles = makeStyles()((theme) =>
({
  container: {
    width: '100%',
    marginTop: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
  },
  responsiveFlexContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  form: {
    width: '90%',
  },
  formElementContainer: {
    marginBottom: theme.spacing(2),
    width: '45%',
    marginRight: '5%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  label: {
    color: theme.palette.text.secondary,
  },
  submitButtonContainer: {
    marginTop: theme.spacing(3),
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
}));

const datePickerFormatConvertor = (date: string) =>
  dayjs(date, 'ddd, DD MMM YYYY hh:mm:ss').format(PAYLOAD_DATE_FORMAT);

interface InitialData {
  startDate: string,
  endDate: string,
  startTime: string,
  endTime: string,
  lastUpdatedBy: string,
  lastUpdatedAt: string,
  examLevelCode: string,
  configurationType: string,
}

const ResultReleaseConfiguration = ({
  initData,
  isEnableEdit,
}: {
  initData: OpenPeriodConfigRequest;
  isEnableEdit: boolean;
}) => {
  const { classes } = useStyles();

  const { t } = useTranslation();

  const [initialData, setInitialData] = useState<InitialData>({
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    lastUpdatedBy: '',
    lastUpdatedAt: '',
    examLevelCode: '',
    configurationType: '',
  });
  const [dateChanged, setDateChanged] = useState(true);

  const { form, updateOpenPeriodMutation, submitUpdate } = useFeatureConfiguration(
    initialData.configurationType,
    initialData.examLevelCode,
    initialData.lastUpdatedBy,
    initialData.lastUpdatedAt
  );

  const { handleSubmit, control, formState: { errors }, watch, trigger } = form;

  const { mutate, isSuccess, data } = candidateConfigQuery.useOpenPeriodConfig((res) => {
    setInitialData({
      startDate: stringToPayloadFormat(res.data.startDateTime),
      endDate: stringToPayloadFormat(res.data.endDateTime),
      startTime: stringToShortTime(res.data.startDateTime),
      endTime: stringToShortTime(res.data.endDateTime),
      lastUpdatedBy: res.data.lastUpdatedBy,
      lastUpdatedAt: res.data.lastUpdatedAt,
      examLevelCode: res.data.examLevelCode,
      configurationType: res.data.configurationType,
    });
    form.setValue('startDate', stringToPayloadFormat(res.data.startDateTime));
    form.setValue('endDate', stringToPayloadFormat(res.data.endDateTime));
    form.setValue('startTime', stringToShortTime(res.data.startDateTime));
    form.setValue('endTime', stringToShortTime(res.data.endDateTime));
  });

  const prevConfigurationType = usePrevious(initData.configurationType);
  const prevExamLevelCode = usePrevious(initData.examLevelCode);
  useEffect(() => {
    if (
      prevConfigurationType !== initData.configurationType ||
      initData.examLevelCode !== prevExamLevelCode
    ) {
      // eslint-disable-next-line no-underscore-dangle
      updateOpenPeriodMutation._reset();
    }
  }, [initData, prevConfigurationType, prevExamLevelCode, updateOpenPeriodMutation]);

  useEffect(() => {
    if (initData.configurationType !== '' || initData.examLevelCode !== '') {
      mutate({
        configurationType: initData.configurationType,
        examLevelCode: initData.examLevelCode,
      });
      setDateChanged(true);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [initData, updateOpenPeriodMutation.status]); // DO NO PUT mutate TO FIX THE ESLINT

  const watchStartDate = watch('startDate', initialData.startDate);
  const watchStartTime = watch('startTime', initialData.startTime);
  const watchEndDate = watch('endDate', initialData.endDate);
  const watchEndTIme = watch('endTime', initialData.endTime);

  // Logic to check end date if start date is changed, exceed year hard limit
  useEffect(() => {
    if (watchStartDate) {
      // Check if the endDate value in the form is null if is null then need to set
      // to the correct value if not JOI cannot do validation
      if (!form.getValues().endDate) {
        form.setValue('endDate', initialData.endDate);
      }
      trigger(['startDate', 'endDate']);
    }
    if (watchEndDate) {
      // Check if the startdate value in the form is null if is null then need to set
      // to the correct value if not JOI cannot do validation
      if (!form.getValues().startDate) {
        form.setValue('startDate', initialData.startDate);
      }
      trigger('endDate');
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [watchStartDate, watchEndDate, trigger, initialData.startDate, initialData.endDate]);

  // Logic to check if startDateTime & endDateTime Changed
  useEffect(() => {
    if (watchStartDate && watchStartTime && data && watchEndDate && watchEndTIme) {
      const startDateTime = watchStartDate.concat(watchStartTime);
      const endDateTime = watchEndDate.concat(watchEndTIme);
      if (startDateTime !== data.data.startDateTime) {
        setDateChanged(false);
      } else if (endDateTime !== data.data.endDateTime) {
        setDateChanged(false);
      } else {
        setDateChanged(true);
      }
    }
  }, [watchStartTime, watchStartDate, watchEndDate, watchEndTIme, data]);

  return (
    <Box className={classes.container}>
      {isSuccess && (
        <form
          onSubmit={handleSubmit((formData: any) => {
            submitUpdate(formData);
          })}
          className={`${classes.form} ${classes.responsiveFlexContainer}`}
        >
          <ControlledDatePicker
            id="inputText-startDate"
            label={t('admin->>ResultsReleaseConfiguration.Start Date')}
            onChange={(date) => {
              setInitialData({ ...initialData, startDate: date  || ''});
              form.setValue('startDate', date || '');
            }}
            control={control}
            name="startDate"
            className={classes.formElementContainer}
            error={!!errors.startDate}
            helperText={errors?.startDate?.message?.toString()}
            inputVariant="filled"
            autoOk
          />
          <TextFieldForm
            form={form}
            label={t('admin->>ResultsReleaseConfiguration.Start Time')}
            name="startTime"
            type="time"
            className={classes.formElementContainer}
            error={!!errors.startTime}
            helperText={errors?.startTime?.message?.toString()}
          />
          <ControlledDatePicker
            id="inputText-endDate"
            label={t('admin->>ResultsReleaseConfiguration.End Date')}
            control={control}
            onChange={(date) => {
              setInitialData({ ...initialData, endDate: date || '' });
              form.setValue('endDate', date || '');
            }}
            name="endDate"
            className={classes.formElementContainer}
            error={!!errors.endDate}
            helperText={errors?.endDate?.message?.toString()}
            inputVariant="filled"
            autoOk
          />
          <TextFieldForm
            form={form}
            label={t('admin->>ResultsReleaseConfiguration.End Time')}
            name="endTime"
            type="time"
            className={classes.formElementContainer}
            error={!!errors.endTime}
            helperText={errors?.endTime?.message?.toString()}
          />
          {initialData.lastUpdatedAt !== null && initialData.lastUpdatedBy !== null && (
            <Typography variant="subtitle2" className={classes.label}>
              {`This data was last updated by ${initialData.lastUpdatedBy} on ${dayjs(
                initialData.lastUpdatedAt
              ).format('DD/MM/YYYY HH:mm')}.`}
            </Typography>
          )}

          <Box className={classes.submitButtonContainer}>
            {isEnableEdit && (
              <ApiButton
                id="button-featureConfiguration-update"
                variant="outlined"
                disabled={dateChanged}
                status={updateOpenPeriodMutation.status}
                type="submit"
              >
                {t('admin->>ResultsReleaseConfiguration.Update')}
              </ApiButton>
            )}
          </Box>
        </form>
      )}
    </Box>
  );
};

export default ResultReleaseConfiguration;
